import { Inject, Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Observable, of, forkJoin } from 'rxjs';
import { share, map, catchError, switchMap, mergeMap } from 'rxjs/operators';
import { AgreementsAction } from './agreements.action';
import { BackendService } from '../../services/backend/backend.service';
import { DefaultResponse } from '../../models/form/DefaultResponse';
import { RoutesTable } from '../../resources/routes-table.model';
import { ErrorsType } from '../../enum/errors.type';
import { Store } from '@ngrx/store';
import { AppState } from '../../interfaces/app-state.interface';
import { NetworkService } from '../../services/network.services';
import { ApiProvider } from '../../../shared/services/new-http/providers/api.http.service';
import { SettingsService } from '../../../shared/services/settings/settings.service';
import { AgreementsApiVersion } from '../../../shared/enum/agreement/agreements-options.enum';
import { HttpHeaders } from '@angular/common/http';


@Injectable()
export class AgreementsEffects {


    private authenticationToken: string;
    private sessionIDUSer: any;


    get headers(): HttpHeaders {
        this.store.select(s => s.authentication).subscribe(data => {
            this.authenticationToken = data.currentUser ? data.currentUser.token : null;
            this.sessionIDUSer = data.sessionID ? data.sessionID : '';
        });
        if (this.authenticationToken) {
            return new HttpHeaders({
                'Content-Type': 'application/json; charset=UTF-8',
                'Authorization': `Bearer ${this.authenticationToken}`,
            });
        }
        else {
            return new HttpHeaders({
                'Content-Type': 'application/json; charset=UTF-8',
                'X-SessionID': `${this.sessionIDUSer}`,
            });
        }
    }


    get apiVersion(): AgreementsApiVersion {

        const settings =
            this.settingsService.getConfigurationKeyValue("backendApiVersion");

        if (!settings) {
            return AgreementsApiVersion.VERSION_1;
        }

        return settings.agreement;
    }


    /** Effect: Recupera um agreement */
    @Effect() public getAgreement$ = this.actions$
        .pipe(
          ofType(AgreementsAction.getAgreement),
            switchMap((action: any) => {
                return this.getAgreement(action.payload)
                    .pipe(
                        map((result) => {
                            if (result.ok) {
                                return { type: AgreementsAction.getAgreementSuccess, payload: result.value };
                            }
                            return { type: AgreementsAction.getAgreementSuccess, payload: result.value };
                        }),
                        catchError((error) => {
                            return of({ type: AgreementsAction.getAgreementError });
                        }),
                    );
            }),
            share(),
        );

    /** Effect: Recupera um agreement e adiciona na lista */
    @Effect() public getAgreementData$ = this.actions$
        .pipe(
            ofType(AgreementsAction.getAgreementData),
            mergeMap((action: any) => {
                return this.getAgreement(action.payload)
                    .pipe(
                        map((result) => {
                            if (result.ok) {
                                return { type: AgreementsAction.getAgreementDataSuccess, payload: result.value };
                            }

                            return { type: AgreementsAction.getAgreementDataError, payload: result.value };
                        }),
                        catchError((error) => {
                            return of({ type: AgreementsAction.getAgreementDataError })
                        }),
                    );
            }),
            share(),
        );


    /** Effect: Aceitar contratro */
    @Effect() public acceptAgreement$ = this.actions$
        .pipe(
            ofType(AgreementsAction.acceptAgreement),
            mergeMap((action: any) => {
                return this.acceptAgreement(action.payload)
                .pipe(
                    map((payload) => {
                        if (payload.hasError) {
                            return { type: AgreementsAction.acceptAgreementError, payload: { type: ErrorsType.Error, value: payload.messages }}
                        }
                        if (payload.ok && payload.hasValue) {
                            return { type: AgreementsAction.acceptAgreementSuccess, payload: payload }
                        }
                        return { type: AgreementsAction.acceptAgreementSuccess, payload: action.payload };
                    }),
                    catchError((error) => of({ type: AgreementsAction.acceptAgreementError, payload: error })),
                );
            }),
            share(),
        );



    /** Effect: contratos já aceitos pelo usuário */
    @Effect() public getAccepted$ = this.actions$
    .pipe(
          ofType(AgreementsAction.getAccepted),
            switchMap((action) => {
                return this.getAcceptedAgreements()               
                    .pipe(
                        map((result) => {
                            if (result.hasAlerta) {
                                return {
                                    type: AgreementsAction.getAcceptedError,
                                    payload: {
                                        type: ErrorsType.Warning,
                                        value: result.messages,
                                    }
                                };
                            }
                            if (result.hasError) {
                                return { type: AgreementsAction.getAcceptedError, payload: { type: ErrorsType.Error, value: result.messages } };
                            }
                            if (result.ok && result.hasValue) {
                                return { type: AgreementsAction.getAcceptedSuccess, payload: result.value };
                            }
                            return { type: AgreementsAction.getAcceptedSuccess, payload: result.value };
                        }),
                        catchError((error) => of({ type: AgreementsAction.getAcceptedError, payload: error })),
                    );
            }),
            share(),
        );

    @Effect() public downloadAgreement$ = this.actions$
    .pipe(
      ofType(AgreementsAction.downloadAgreement),
            mergeMap((action: any) => {
                return this.backendService.get<DefaultResponse>(`corporation/file?fileCode=${action.payload}`)
                    .toPromise()
                    .then((result: any) => {
                        if (result.ok) {
                            // let name = `contrato.pdf`;
                            // var link = document.createElement('a');
                            // link.href = result.value.serverURL;
                            // link.target = "_blank";
                            // link.download = name;
                            // link.click();
                            this.networkService.downloadFile(result.value, result.value.name);
                            return { type: AgreementsAction.downloadAgreementSuccess, payload: result.value };
                        }
                        return { type: AgreementsAction.downloadAgreementError, payload: result.value };
                    })
                    .catch((error) => of({ type: AgreementsAction.downloadAgreementError, payload: error }));
            }),
            share(),
        );

    @Effect() public acceptMultipleAgreements$ = this.actions$
        .pipe(
            ofType(AgreementsAction.acceptMultipleAgreements),
            mergeMap(({ payload }) => {
                return this.acceptMultipleAgreements(payload)
                    .pipe(
                        map((result) => {
                            const allResponsesAreOk = (result as any)
                            .reduce((allOk, { ok }) =>  ok && allOk, true);

                            if (allResponsesAreOk) {
                                return {
                                    type: AgreementsAction.acceptMultipleAgreementsSuccess,
                                    payload: (result as any).reduce((msgs, { messages }) => [...messages, ...msgs], []),
                                };
                            }

                            return {
                                type: AgreementsAction.acceptMultipleAgreementsError,
                                payload: (result as any).filter(({ ok }) => !ok),
                            };
                        }),
                    );
            }),
        );

    @Effect() public getMultipleAgreementsData$ = this.actions$
        .pipe(
            ofType(AgreementsAction.getMultipleAgreementsData),
            mergeMap(({ payload }) => {
                return this.getMultipleAgreementsData(payload)
                    .pipe(
                        map((result) => {
                            const allResponsesAreOk = (result as any)
                            .reduce((allOk, { ok }) =>  ok && allOk, true);

                            if (allResponsesAreOk) {
                                return {
                                    type: AgreementsAction.getMultipleAgreementsDataSuccess,
                                    payload: (result as any).map(({ value }) => value),
                                };
                            }

                            return {
                                type: AgreementsAction.getMultipleAgreementsDataError,
                                payload: (result as any).filter(({ ok }) => !ok),
                            };
                        }),
                    );
            }),
        );


    @Effect() public getAgreementStatus$ = this.actions$
        .pipe(
            ofType(AgreementsAction.getAgreementStatus),
            switchMap((action) => {
                return this.getAgreementStatus()
                    .pipe(
                        map((result) => {
                            if (result.hasAlerta) {
                                return {
                                    type: AgreementsAction.getAgreementStatusError,
                                    payload: {
                                        type: ErrorsType.Warning,
                                        value: result.messages,
                                    }
                                };
                            }
                            if (result.hasError) {
                                return {
                                    type: AgreementsAction.getAgreementStatusError,
                                    payload: { type: ErrorsType.Error, value: result.messages }
                                };
                            }
                            if (result.ok && result.hasValue) {
                                return { type: AgreementsAction.getAgreementStatusSuccess, payload: result.value };
                            }
                            return { type: AgreementsAction.getAgreementStatusSuccess, payload: result.value };
                        }),
                        catchError((error) => of({ type: AgreementsAction.getAgreementStatusError, payload: error })),
                    );
            }),
            share(),
        );

    constructor(
        private actions$: Actions<AgreementsAction>,
        private backendService: BackendService,
        private store: Store<AppState>,
        private networkService: NetworkService,
        private api: ApiProvider,
        private settingsService: SettingsService,
    ) { }
    /**
     * Aceita um contrato
     * @param data dados
     */
    private acceptAgreement(agreement: any) {
        if (this.apiVersion === AgreementsApiVersion.VERSION_1_1) {
            return this.api.post<any>(
                RoutesTable.agreementV1_1.routes.acceptAgreement.uri, agreement, { headers: this.headers }
            );
        }
        return this.backendService.post<any>('agreement/accept', agreement);
    }

    /**
     * Recupera um agreement com base em seu id
     * @param agreementId id do contrato
     */
    private getAgreement(agreementId: number | string) {
        if (this.apiVersion === AgreementsApiVersion.VERSION_1_1) {
            return this.api.get<any>(
                RoutesTable.agreementV1_1.routes.getAgreement.uri + agreementId, { headers: this.headers }
            );
        }
        return this.backendService.get<DefaultResponse>(`agreement/${agreementId}`);
    }

    
    /**
     * Baixa um agreement com base em seu id
     * @param agreementId id do contrato
     */
    private acceptMultipleAgreements(agreements: number[] | string[]) {
        
        return forkJoin(
                //@ts-ignore
                ...agreements.map((agreementId: any) => this.acceptAgreement(agreementId))
            );
    }

    /**
     * Recupera os contratos já aceitos pelo usuário
     */
    private getAcceptedAgreements(): Observable<DefaultResponse> {
        if (this.apiVersion === AgreementsApiVersion.VERSION_1_1) {
            return this.api.get<any>(
                RoutesTable.agreementV1_1.routes.getAcceptedAgreements.uri, { headers: this.headers }
            );
        }

        return this.backendService.get<DefaultResponse>('agreement/accept');
    }

    /**
     * Recupera todos os contratos do usuário, com a informação de status
     * para cada contrato
     */
    private getAgreementStatus(): Observable<DefaultResponse> {
        return this.backendService.get<DefaultResponse>('agreement/status');
    }

    private getMultipleAgreementsData(agreementsId: number[]): Observable<DefaultResponse> {
        return forkJoin(
            ...agreementsId.map((agreementId) =>
            this.getAgreement(agreementId)));
    }

}
